export interface CompanionAgentStatus {
    code: string;
    displayName: string;
    command: string;
    available: boolean;
    version?: string;
    supportsReadOnly: boolean;
}

export interface DesktopAgentCapability {
    code: string;
    displayName: string;
    description: string;
    available: boolean;
    requiresApproval: boolean;
}

export interface DesktopAgentRuntime {
    mode: string;
    transport: string;
    address: string;
    interactiveSessionRequired: boolean;
}

export interface DesktopAgentSecurity {
    loopbackOnly: boolean;
    runsAsCurrentUser: boolean;
    requiresAdministrator: boolean;
    readOnlyByDefault: boolean;
}

export interface CompanionHealth {
    app: string;
    productCode?: string;
    displayName?: string;
    version: string;
    status?: 'Ready' | 'Limited' | string;
    runtime?: DesktopAgentRuntime;
    security?: DesktopAgentSecurity;
    capabilities?: DesktopAgentCapability[];
    agents: CompanionAgentStatus[];
}

interface CompanionEventPayload {
    text?: string;
    message?: string;
    exitCode?: number;
}

class CompanionService {
    async health(port: number): Promise<CompanionHealth> {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 3_000);
        try {
            const response = await fetch(`http://127.0.0.1:${port}/health`, {
                signal: controller.signal,
                cache: 'no-store',
            });
            if (!response.ok) throw new Error(`Companion non disponibile (${response.status}).`);
            return await response.json() as CompanionHealth;
        } finally {
            window.clearTimeout(timeout);
        }
    }

    async runReadOnly(
        port: number,
        agent: string,
        prompt: string,
        workingDirectory?: string,
        options: { signal?: AbortSignal; onText?: (line: string) => void } = {},
    ): Promise<string> {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 10 * 60_000);
        // Chiudere la connessione basta: il Desktop Agent termina la CLI e i suoi processi figli.
        options.signal?.addEventListener('abort', () => controller.abort(), { once: true });
        let response: Response;
        try {
            response = await fetch(`http://127.0.0.1:${port}/run`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    agent,
                    prompt,
                    workingDirectory: workingDirectory?.trim() || null,
                    readOnly: true,
                }),
                signal: controller.signal,
            });
            if (!response.ok || !response.body) {
                const payload = await response.json().catch(() => null) as { error?: string } | null;
                throw new Error(payload?.error || `Companion non disponibile (${response.status}).`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let output = '';
            let stderr = '';
            let remoteError = '';
            let exitCode: number | null = null;

            const consumeBlock = (block: string) => {
                const lines = block.split('\n');
                const eventName = lines.find(line => line.startsWith('event:'))?.slice(6).trim() || 'message';
                const dataText = lines.filter(line => line.startsWith('data:')).map(line => line.slice(5).trim()).join('\n');
                if (!dataText) return;
                let payload: CompanionEventPayload;
                try { payload = JSON.parse(dataText) as CompanionEventPayload; }
                catch { return; }

                if (eventName === 'text' && payload.text != null) {
                    output += `${payload.text}\n`;
                    options.onText?.(payload.text);
                }
                if (eventName === 'stderr' && payload.text != null) stderr += payload.text;
                if (eventName === 'error') remoteError = payload.message || 'Errore della CLI locale.';
                if (eventName === 'done' && typeof payload.exitCode === 'number') exitCode = payload.exitCode;
            };

            while (true) {
                const { value, done } = await reader.read();
                buffer += decoder.decode(value, { stream: !done }).replace(/\r\n/g, '\n');
                let separator = buffer.indexOf('\n\n');
                while (separator >= 0) {
                    consumeBlock(buffer.slice(0, separator));
                    buffer = buffer.slice(separator + 2);
                    separator = buffer.indexOf('\n\n');
                }
                if (done) break;
            }
            if (buffer.trim()) consumeBlock(buffer);

            if (remoteError) throw new Error(remoteError);
            if (exitCode == null) throw new Error('Il Companion ha terminato lo stream senza esito.');
            if (exitCode !== 0) throw new Error(stderr.trim() || `La CLI è terminata con codice ${exitCode}.`);
            if (!output.trim()) throw new Error('La CLI non ha restituito una proposta.');
            return output.trim();
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError')
                throw new Error('Esecuzione CLI scaduta o annullata.');
            throw error;
        } finally {
            window.clearTimeout(timeout);
        }
    }
}

export default new CompanionService();
